import { defineStore } from 'pinia'
import { api } from '@/plugins/api'
import { authStore } from './auth'

export const compareStore = defineStore('compare', {
  state: () => ({
    loading: {
      compare: false,
      databases: false,
      upload: false,
      bulkCompare: false,
      bulkApply: false,
    },
    databases: [],
    compare: null,
    // File upload state
    uploadSessionId: null,
    uploadedFileName: null,
    tempDatabaseName: null,
    uploadError: null,
    // Bulk comparison state
    bulkResults: [],
    bulkError: null,
  }),
  actions: {
    resetCompare() {
      this.compare = null
    },
    resetUpload() {
      this.uploadSessionId = null
      this.uploadedFileName = null
      this.tempDatabaseName = null
      this.uploadError = null
      this.loading.upload = false
    },
    resetBulkCompare() {
      this.bulkResults = []
      this.bulkError = null
    },
    async getDatabases() {
      this.loading.databases = true
      this.databases = []
      let results = await api.get('database/names')
      if (results) {
        this.databases = results
      }
      this.loading.databases = false
      return this.databases
    },
    async getCompare(options) {
      this.loading.compare = true
      this.compare = null
      let results = await api.post('database/compare', options)
      if (results) {
        this.compare = results
      }
      this.loading.compare = false
      return this.compare
    },
    async applyScript(data) {
      return await api.post('database/apply', data)
    },
    async uploadDatabaseFile(file) {
      this.loading.upload = true
      this.uploadError = null
      this.uploadedFileName = null
      this.tempDatabaseName = null
      this.uploadSessionId = null

      try {
        const formData = new FormData()
        formData.append('file', file)

        const auth = authStore()
        const apiUrl = window.config ? window.config.apiUrl : ''

        const response = await fetch(`${apiUrl}/database/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth.token}`
          },
          body: formData
        })

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Upload failed')
        }

        const result = await response.json()
        this.uploadSessionId = result.sessionId
        this.uploadedFileName = file.name

        return {
          success: true,
          sessionId: result.sessionId,
          message: result.message
        }
      } catch (error) {
        this.uploadError = error.message
        this.loading.upload = false
        return {
          success: false,
          error: error.message
        }
      }
    },
    async cleanupTempDatabase(tempDatabaseName) {
      if (!tempDatabaseName) return

      try {
        const auth = authStore()
        const apiUrl = window.config ? window.config.apiUrl : ''

        const response = await fetch(`${apiUrl}/database/cleanup/${tempDatabaseName}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${auth.token}`
          }
        })

        if (response.ok) {
          const result = await response.json()
          console.log('Temp database cleanup:', result.message)
          return { success: true, message: result.message }
        } else {
          const error = await response.json()
          console.warn('Cleanup failed (non-critical):', error.error)
          return { success: false, error: error.error }
        }
      } catch (error) {
        console.warn('Cleanup error (non-critical):', error.message)
        return { success: false, error: error.message }
      }
    },
    // Called by SignalR when database creation completes
    handleUploadComplete(data) {
      this.loading.upload = false

      if (data.success) {
        this.tempDatabaseName = data.tempDatabaseName
      } else {
        this.uploadError = data.message
        this.uploadedFileName = null
        this.uploadSessionId = null
      }
    },
    async compareBulk(options) {
      this.loading.bulkCompare = true
      this.bulkResults = []
      this.bulkError = null

      try {
        const auth = authStore()
        const apiUrl = window.config ? window.config.apiUrl : ''

        const response = await fetch(`${apiUrl}/database/compare-bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`
          },
          body: JSON.stringify({
            templateDatabase: options.templateDatabase,
            type: options.type,
            group: options.group
          })
        })

        const data = await response.json()

        if (data.error) {
          throw new Error(data.error)
        }

        // Add selected property to each result for UI
        this.bulkResults = data.map(result => ({
          ...result,
          selected: false
        }))

        return { success: true, results: this.bulkResults }
      } catch (error) {
        this.bulkError = error.message
        return { success: false, error: error.message }
      } finally {
        this.loading.bulkCompare = false
      }
    },
    async applyBulkScript(selectedResults) {
      this.loading.bulkApply = true
      const errors = []
      let successCount = 0

      try {
        for (const result of selectedResults) {
          try {
            const data = {
              script: result.script,
              databases: [result.databaseName]
            }

            const applyResult = await this.applyScript(data)

            if (applyResult && applyResult.indexOf && applyResult.indexOf('error') !== -1) {
              errors.push(`${result.databaseName}: ${applyResult}`)
            } else {
              successCount++
            }
          } catch (err) {
            errors.push(`${result.databaseName}: ${err.message}`)
          }
        }

        this.loading.bulkApply = false

        return {
          success: errors.length === 0,
          successCount,
          errors
        }
      } catch (error) {
        this.loading.bulkApply = false
        return {
          success: false,
          successCount,
          errors: [...errors, error.message]
        }
      }
    },
  },
})
